import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal, X } from 'lucide-react';
import { DecoratorNode, $createParagraphNode, $getNodeByKey, $getRoot } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  ReportChartVisual,
  ReportImageGrid,
} from '../../Shared/ReportVisualBlocks';
import { ReportDraftVisualModifyDialog } from '../toolbar/ReportDraftVisualModifyDialog';

const sanitizeData = (data) => {
  if (!data || typeof data !== 'object') {
    return {};
  }
  const chart = data.chart && typeof data.chart === 'object'
    ? {
      ...data.chart,
      ...(Array.isArray(data.chart.series) ? { series: data.chart.series } : {}),
      ...(data.chart.xAxis && typeof data.chart.xAxis === 'object' ? { xAxis: data.chart.xAxis } : {}),
      ...(data.chart.yAxis && typeof data.chart.yAxis === 'object' ? { yAxis: data.chart.yAxis } : {}),
      ...(typeof data.chart.showLegend === 'boolean' ? { showLegend: data.chart.showLegend } : {}),
    }
    : null;
  return {
    ...data,
    imageList: Array.isArray(data.imageList) ? data.imageList : [],
    chart,
    source: data.source && typeof data.source === 'object' ? data.source : undefined,
    visualType: data.visualType || data.type || 'chart',
  };
};

const joinZoneLabels = (labels) => {
  if (!Array.isArray(labels)) {
    return '';
  }
  return labels.filter(Boolean).join(', ');
};

const normalizeCaption = (value) => String(value || '')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

const captionsMatch = (left, right) => {
  const normalizedLeft = normalizeCaption(left);
  const normalizedRight = normalizeCaption(right);
  return Boolean(normalizedLeft && normalizedRight && normalizedLeft === normalizedRight);
};

function CaptionField({
  label,
  value,
  placeholder,
  readOnly,
  onChange,
  rows = 5,
}) {
  return (
    <div className="report-draft-editor__visual-caption">
      {label ? <label className="report-draft-editor__visual-caption-label">{label}</label> : null}
      {readOnly ? (
        <p className="report-draft-editor__visual-caption-text">{value || ''}</p>
      ) : (
        <textarea
          className="report-draft-editor__visual-caption-input"
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder || ''}
          rows={rows}
        />
      )}
    </div>
  );
}

function ReportVisualBlockComponent({ nodeKey, data }) {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const [isModifyOpen, setIsModifyOpen] = useState(false);
  const nodeData = useMemo(() => sanitizeData(data), [data]);
  const readOnly = !editor.isEditable();
  const isOfficialPrintMode = typeof document !== 'undefined'
    && document.body?.dataset?.reportExportProfile === 'official_v1';

  const updateData = useCallback(
    (updater) => {
      if (readOnly) {
        return;
      }
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (!$isReportVisualNode(node)) {
          return;
        }
        const current = sanitizeData(node.getData());
        const nextData = typeof updater === 'function' ? updater(current) : updater;
        node.replaceData(sanitizeData(nextData));
      });
    },
    [editor, nodeKey, readOnly]
  );

  const updateCaption = useCallback(
    (value) => {
      updateData((current) => ({
        ...current,
        caption: value,
      }));
    },
    [updateData]
  );

  const updateImageCaption = useCallback(
    (index, value) => {
      updateData((current) => {
        const images = Array.isArray(current.imageList) ? current.imageList : [];
        const nextImages = images.map((image, imageIndex) => (
          imageIndex === index
            ? {
              ...image,
              caption: value,
            }
            : image
        ));
        return {
          ...current,
          imageList: nextImages,
        };
      });
    },
    [updateData]
  );

  const deleteNode = useCallback(() => {
    if (readOnly) {
      return;
    }
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (!$isReportVisualNode(node)) {
        return;
      }
      node.remove();
      const root = $getRoot();
      if (root.getChildren().length === 0) {
        root.append($createParagraphNode());
      }
    });
  }, [editor, nodeKey, readOnly]);

  const applyModifiedNodeData = useCallback((nextNodeData) => {
    updateData(nextNodeData);
  }, [updateData]);

  const visualType = nodeData.visualType;
  const captionLabel = nodeData.captionLabel || t('reports.review.template.placeholders.caption');
  const captionPlaceholder = nodeData.captionPlaceholder || '';
  const figureCaption = nodeData.caption || '';
  const chartHeight = readOnly && isOfficialPrintMode
    ? (visualType === 'map' ? 372 : 228)
    : (visualType === 'map' ? 346 : 236);
  const shouldHideFigureCaption = readOnly
    && Array.isArray(nodeData.imageList)
    && nodeData.imageList.length > 0
    && nodeData.imageList.every(image => captionsMatch(image?.caption, figureCaption));
  const visualBlockClassName = [
    'report-draft-editor__visual-block',
    `report-draft-editor__visual-block--${visualType}`,
    readOnly ? 'report-draft-editor__visual-block--readonly' : 'report-draft-editor__visual-block--editable',
  ].join(' ');
  const renderImageFooter = ({ image, index }) => {
    if (readOnly && captionsMatch(image?.caption, figureCaption)) {
      return null;
    }
    return (
      <CaptionField
        label={captionLabel}
        value={image?.caption || ''}
        readOnly={readOnly}
        onChange={(nextValue) => updateImageCaption(index, nextValue)}
        rows={5}
      />
    );
  };

  return (
    <figure
      className={visualBlockClassName}
      data-visual-type={visualType}
      data-report-readonly={readOnly ? 'true' : 'false'}
      contentEditable={false}
    >
      {!readOnly ? (
        <div className="report-draft-editor__visual-actions" role="toolbar" aria-label={t('reports.review.editor.visualActions.label', { defaultValue: 'Visual block actions' })}>
          {(visualType === 'chart' || visualType === 'map') ? (
            <button
              type="button"
              className="report-draft-editor__visual-action-btn"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsModifyOpen(true);
              }}
              aria-label={t('reports.review.editor.visualActions.modify', { defaultValue: 'Modify visual' })}
              title={t('reports.review.editor.visualActions.modify', { defaultValue: 'Modify visual' })}
            >
              <SlidersHorizontal size={14} />
            </button>
          ) : null}
          <button
            type="button"
            className="report-draft-editor__visual-action-btn report-draft-editor__visual-action-btn--danger"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              deleteNode();
            }}
            aria-label={t('reports.review.editor.visualActions.delete', { defaultValue: 'Delete visual' })}
            title={t('reports.review.editor.visualActions.delete', { defaultValue: 'Delete visual' })}
          >
            <X size={14} />
          </button>
        </div>
      ) : null}

      {nodeData.title ? (
        <div className="report-draft-editor__visual-title">{nodeData.title}</div>
      ) : null}

      {visualType === 'map' || visualType === 'chart' ? (
        <div className={`report-draft-editor__visual-chart-wrap report-draft-editor__visual-chart-wrap--${visualType}`}>
          <ReportChartVisual
            chart={nodeData.chart}
            fallbackCoverage={nodeData.fallbackCoverage}
            emptyLabel={t('reports.workspace.blocks.emptyChart')}
            height={chartHeight}
          />
        </div>
      ) : null}

      {visualType === 'map' && joinZoneLabels(nodeData.zoneLabels) ? (
        <div className="report-draft-editor__visual-meta">
          <strong>{nodeData.zoneLabelLabel || t('reports.review.template.placeholders.zoneLabels')}:</strong>{' '}
          {joinZoneLabels(nodeData.zoneLabels)}
        </div>
      ) : null}

      {visualType === 'image' ? (
        <div className="report-draft-editor__visual-images">
          <ReportImageGrid
            images={nodeData.imageList}
            className="report-draft-editor__visual-image-grid"
            emptyLabel={t('reports.workspace.blocks.emptyImages')}
            altFallback={nodeData.title}
            renderImageFooter={renderImageFooter}
          />
        </div>
      ) : null}

      {!shouldHideFigureCaption ? (
        <figcaption className="report-draft-editor__visual-figcaption">
          <CaptionField
            label={captionLabel}
            value={figureCaption}
            placeholder={captionPlaceholder}
            readOnly={readOnly}
            onChange={updateCaption}
            rows={5}
          />
        </figcaption>
      ) : null}

      {!readOnly && (visualType === 'chart' || visualType === 'map') ? (
        <ReportDraftVisualModifyDialog
          open={isModifyOpen}
          onOpenChange={setIsModifyOpen}
          nodeData={nodeData}
          visualType={visualType}
          onApply={applyModifiedNodeData}
        />
      ) : null}
    </figure>
  );
}

export class ReportVisualNode extends DecoratorNode {
  __data;

  static getType() {
    return 'report-visual';
  }

  static clone(node) {
    return new ReportVisualNode(node.__data, node.__key);
  }

  static importJSON(serializedNode) {
    return $createReportVisualNode(serializedNode.data || {});
  }

  constructor(data, key) {
    super(key);
    this.__data = sanitizeData(data);
  }

  exportJSON() {
    return {
      type: 'report-visual',
      version: 1,
      data: this.__data,
    };
  }

  createDOM() {
    const dom = document.createElement('div');
    dom.className = 'report-draft-editor__visual-node';
    return dom;
  }

  updateDOM() {
    return false;
  }

  decorate() {
    return <ReportVisualBlockComponent nodeKey={this.getKey()} data={this.__data} />;
  }

  isInline() {
    return false;
  }

  isIsolated() {
    return true;
  }

  getData() {
    return sanitizeData(this.getLatest().__data);
  }

  replaceData(nextData) {
    const writable = this.getWritable();
    writable.__data = sanitizeData(nextData);
  }
}

export function $createReportVisualNode(data) {
  return new ReportVisualNode(data);
}

export function $isReportVisualNode(node) {
  return node instanceof ReportVisualNode;
}
